// TKNE — the AI architect embedded in the O.R.D.E.R project screen. Thin
// client: it POSTs to the shared /api/ai/daedalus backend, which fills the
// `designs` row server-side via Claude tool-use. When the response reports
// fieldUpdates we call onApplied() so the parent re-fetches the row and the
// ORDER sections refresh.
//
// Flow: opening the panel first shows a column of the four working modes,
// each with a plain-language explanation. Picking one opens a chat thread
// that starts with TKNE introducing itself, explaining what it's about to
// do, and asking for the info it needs to get going.
import React, { useRef, useState } from 'react';
import {
  ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View,
} from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { File } from 'expo-file-system';
import { colors, radii, neu, fonts } from '../../domain/tokens';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabase';

// Claude vision only accepts these four raster formats.
const IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const MAX_IMAGE_BYTES = 4 * 1024 * 1024; // 4MB — comfortably under Claude's 5MB/image cap
const MAX_TEXT_BYTES = 500 * 1024; // matches the web TKNE panel's file-attach cap

type Attachment =
  | { kind: 'image'; name: string; mediaType: string; data: string }
  | { kind: 'text'; name: string; content: string };

async function readAsBase64(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const blob = await (await fetch(uri)).blob();
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return dataUrl.split(',')[1] ?? '';
  }
  return new File(uri).base64();
}

async function readAsText(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    return (await fetch(uri)).text();
  }
  return new File(uri).text();
}

type Mode = 'manual' | 'guided' | 'template' | 'direct';

const MODES: { key: Mode; label: string; blurb: string }[] = [
  {
    key: 'manual',
    label: 'Manual',
    blurb: 'You fill out the form. TKNE only answers questions, suggests content, or critiques what you’ve written when you ask.',
  },
  {
    key: 'guided',
    label: 'Guided',
    blurb: 'TKNE interviews you field by field, in order, and fills each one in as you answer. Say "skip" to move on.',
  },
  {
    key: 'template',
    label: 'Template',
    blurb: 'Describe the kind of project this is. TKNE proposes the typical stages and steps, you approve, then it fills everything.',
  },
  {
    key: 'direct',
    label: 'Direct',
    blurb: 'Describe the project in your own words. TKNE fills the entire form in one pass and reports back what it set.',
  },
];

const WELCOME_MESSAGES: Record<Mode, string> = {
  manual:
    'Hey, I’m TKNE. In Manual mode I stay out of your way — ask me for advice on any field, a critique of what you’ve written, or how sections connect, and I’ll answer. I won’t change anything unless you tell me to. What do you want help with first?',
  guided:
    'Hey, I’m TKNE. I’ll walk you through your O.R.D.E.R form one field at a time, starting with Orient. Answer each question as I ask it (or say "skip") and I’ll fill it in as we go. Ready? First: what problem is this project solving?',
  template:
    'Hey, I’m TKNE. Tell me what kind of project this is and I’ll pull together the common stages, steps, and requirements for a project like it. You’ll review and approve the list before I fill anything in. What’s the project?',
  direct:
    'Hey, I’m TKNE. Describe your project — as much or as little detail as you have — and I’ll fill out the entire O.R.D.E.R form for you. I’ll confirm my understanding first, then fill everything and report back. What’s the project?',
};

type Msg = { role: 'user' | 'assistant'; content: string; isError?: boolean };

type TkneResponse = { conversationId: string; message: string; fieldUpdates?: string[] };

type ChatSummary = { id: string; title: string; created_at: string; updated_at: string };

type DesignFile = {
  id: string;
  name: string;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
};

export function TknePanel({
  designId, viewMode, onApplied,
}: {
  designId: string;
  viewMode: 'basic' | 'advanced';
  onApplied: () => void;
}) {
  const userId = useAuth().session?.user.id;
  const [open, setOpen] = useState(true);
  const [stage, setStage] = useState<'select' | 'chat' | 'chats' | 'files'>('select');
  const [mode, setMode] = useState<Mode | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [chatList, setChatList] = useState<ChatSummary[]>([]);
  const [chatListLoading, setChatListLoading] = useState(false);
  const [fileList, setFileList] = useState<DesignFile[]>([]);
  const [fileListLoading, setFileListLoading] = useState(false);
  const conversationId = useRef<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  const openChats = async () => {
    setStage('chats');
    if (!userId) return;
    setChatListLoading(true);
    try {
      const { data, error } = await supabase
        .from('chat_conversations')
        .select('id, title, created_at, updated_at')
        .eq('user_id', userId)
        .like('title', `tkne:${designId}:%`)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setChatList(data ?? []);
    } catch (e) {
      console.warn('[TknePanel] failed to load chats:', e);
      setChatList([]);
    } finally {
      setChatListLoading(false);
    }
  };

  const openFiles = async () => {
    setStage('files');
    if (!userId) return;
    setFileListLoading(true);
    try {
      const { data, error } = await supabase
        .from('design_files')
        .select('id, name, storage_path, mime_type, size_bytes, created_at')
        .eq('design_id', designId)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFileList(data ?? []);
    } catch (e) {
      console.warn('[TknePanel] failed to load files:', e);
      setFileList([]);
    } finally {
      setFileListLoading(false);
    }
  };

  const openChat = async (chat: ChatSummary) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('id, role, content')
        .eq('conversation_id', chat.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      const loaded: Msg[] = (data ?? [])
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
      conversationId.current = chat.id;
      setMessages(loaded);
      setAttachments([]);
      if (!mode) setMode('manual');
      setStage('chat');
    } catch (e) {
      console.warn('[TknePanel] failed to load chat messages:', e);
    }
  };

  const deleteFile = async (file: DesignFile) => {
    if (!userId) return;
    try {
      await supabase.storage.from('tkne-files').remove([file.storage_path]);
      await supabase.from('design_files').delete().eq('id', file.id);
      await supabase.from('design_logs').insert({
        user_id: userId, design_id: designId, kind: 'change', text: `Deleted file "${file.name}"`,
      });
    } catch (e) {
      console.warn('[TknePanel] failed to delete file:', e);
    } finally {
      openFiles();
    }
  };

  const pickAttachment = async () => {
    setAttachError(null);
    const result = await DocumentPicker.getDocumentAsync({ type: '*/*', multiple: true, copyToCacheDirectory: true });
    if (result.canceled || !result.assets?.length) return;
    setAttaching(true);
    try {
      for (const asset of result.assets) {
        const name = asset.name || 'attachment';
        const mimeType = asset.mimeType || '';
        let read = false;
        if (IMAGE_MIME_TYPES.has(mimeType)) {
          if ((asset.size ?? 0) > MAX_IMAGE_BYTES) {
            setAttachError(`"${name}" is too large (max 4MB for images).`);
            continue;
          }
          const data = await readAsBase64(asset.uri);
          setAttachments((prev) => [...prev, { kind: 'image', name, mediaType: mimeType, data }]);
          read = true;
        } else {
          if ((asset.size ?? 0) > MAX_TEXT_BYTES) {
            setAttachError(`"${name}" is too large (max 500KB for documents).`);
            continue;
          }
          try {
            const content = await readAsText(asset.uri);
            setAttachments((prev) => [...prev, { kind: 'text', name, content }]);
            read = true;
          } catch {
            setAttachError(`Could not read "${name}" — only images and text-based files are supported.`);
          }
        }
        if (read && userId) {
          try {
            const path = `${userId}/${designId}/${Date.now()}_${name}`;
            const res = await fetch(asset.uri);
            const buf = await res.arrayBuffer();
            const { error: uploadError } = await supabase.storage
              .from('tkne-files')
              .upload(path, buf, { contentType: mimeType || 'application/octet-stream' });
            if (uploadError) throw uploadError;
            await supabase.from('design_files').insert({
              user_id: userId,
              design_id: designId,
              name,
              storage_path: path,
              mime_type: mimeType || null,
              size_bytes: asset.size ?? null,
            });
            await supabase.from('design_logs').insert({
              user_id: userId, design_id: designId, kind: 'change', text: `Uploaded file "${name}"`,
            });
          } catch (e) {
            console.warn('[TknePanel] failed to auto-upload attachment:', e);
          }
        }
      }
    } finally {
      setAttaching(false);
    }
  };

  const removeAttachment = (name: string) => {
    setAttachments((prev) => prev.filter((a) => a.name !== name));
  };

  const chooseMode = (m: Mode) => {
    setMode(m);
    setMessages([{ role: 'assistant', content: WELCOME_MESSAGES[m] }]);
    conversationId.current = null;
    setStage('chat');
  };

  const backToModes = () => {
    setStage('select');
    setMode(null);
    setMessages([]);
    setAttachments([]);
    conversationId.current = null;
  };

  const startNewChat = () => {
    if (!mode) return;
    setMessages([{ role: 'assistant', content: WELCOME_MESSAGES[mode] }]);
    setAttachments([]);
    conversationId.current = null;
  };

  const send = async () => {
    const message = input.trim();
    if ((!message && attachments.length === 0) || sending || !mode) return;
    const outgoing = attachments;
    const fileNames = outgoing.map((a) => a.name);
    const displayContent = fileNames.length > 0
      ? `${message || '(attached files)'}\n📎 ${fileNames.join(', ')}`
      : message;
    setInput('');
    setAttachments([]);
    setMessages((p) => [...p, { role: 'user', content: displayContent }]);
    setSending(true);
    try {
      const data = await apiFetch<TkneResponse>('/api/ai/daedalus', {
        method: 'POST',
        body: JSON.stringify({
          message: message || 'Please review the attached file(s).',
          conversationId: conversationId.current,
          designId,
          mode,
          viewMode,
          attachments: outgoing,
        }),
      });
      conversationId.current = data.conversationId;
      setMessages((p) => [...p, { role: 'assistant', content: data.message }]);
      if (data.fieldUpdates?.length) onApplied();
    } catch (e) {
      setMessages((p) => [
        ...p,
        { role: 'assistant', content: e instanceof Error ? e.message : 'Something went wrong.', isError: true },
      ]);
    } finally {
      setSending(false);
      requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
    }
  };

  return (
    <View style={styles.card}>
      <Pressable onPress={() => setOpen((o) => !o)} style={styles.bar}>
        <Text style={styles.barText}>TKNE</Text>
        <View style={styles.barRight}>
          {open && stage === 'chat' ? (
            <Pressable onPress={startNewChat} hitSlop={8} style={styles.newBtn}>
              <Text style={styles.newBtnText}>+</Text>
            </Pressable>
          ) : null}
          <Text style={[styles.arrow, open ? styles.arrowOpen : null]}>◀</Text>
        </View>
      </Pressable>

      {open ? (
        <View style={styles.body}>
          {stage === 'select' ? (
            <View style={styles.selectList}>
              <View style={styles.topRow}>
                <Pressable onPress={openChats} style={styles.topPill}>
                  <Text style={styles.topPillText}>Chats</Text>
                </Pressable>
                <Pressable onPress={openFiles} style={styles.topPill}>
                  <Text style={styles.topPillText}>Files</Text>
                </Pressable>
              </View>
              {MODES.map((m) => (
                <Pressable key={m.key} onPress={() => chooseMode(m.key)} style={styles.selectRow}>
                  <View style={styles.selectPill}>
                    <Text style={styles.selectPillText}>{m.label}</Text>
                  </View>
                  <Text style={styles.selectBlurb}>{m.blurb}</Text>
                </Pressable>
              ))}
            </View>
          ) : stage === 'chats' ? (
            <View style={styles.listPane}>
              <Pressable onPress={() => setStage('select')} hitSlop={8}>
                <Text style={styles.backText}>{'‹'} Back</Text>
              </Pressable>
              {chatListLoading ? (
                <ActivityIndicator color={BRAND} style={styles.listLoading} />
              ) : chatList.length === 0 ? (
                <Text style={styles.listEmpty}>No past chats yet.</Text>
              ) : (
                chatList.map((c) => {
                  const prefix = `tkne:${designId}:`;
                  const title = c.title.startsWith(prefix) ? c.title.slice(prefix.length) : c.title;
                  return (
                    <Pressable key={c.id} onPress={() => openChat(c)} style={styles.listRow}>
                      <Text style={styles.listRowTitle} numberOfLines={1}>{title || 'Untitled chat'}</Text>
                      <Text style={styles.listRowMeta}>{(c.created_at || '').slice(0, 10)}</Text>
                    </Pressable>
                  );
                })
              )}
            </View>
          ) : stage === 'files' ? (
            <View style={styles.listPane}>
              <Pressable onPress={() => setStage('select')} hitSlop={8}>
                <Text style={styles.backText}>{'‹'} Back</Text>
              </Pressable>
              {fileListLoading ? (
                <ActivityIndicator color={BRAND} style={styles.listLoading} />
              ) : fileList.length === 0 ? (
                <Text style={styles.listEmpty}>No files uploaded yet.</Text>
              ) : (
                fileList.map((f) => (
                  <View key={f.id} style={styles.fileRow}>
                    <Text style={styles.fileIcon}>
                      {f.mime_type && IMAGE_MIME_TYPES.has(f.mime_type) ? '🖼' : '📄'}
                    </Text>
                    <View style={styles.fileInfo}>
                      <Text style={styles.listRowTitle} numberOfLines={1}>{f.name}</Text>
                      <Text style={styles.listRowMeta}>
                        {f.size_bytes ? `${Math.round(f.size_bytes / 1024)} KB · ` : ''}{(f.created_at || '').slice(0, 10)}
                      </Text>
                    </View>
                    <Pressable onPress={() => deleteFile(f)} hitSlop={6}>
                      <Text style={styles.fileDelete}>✕</Text>
                    </Pressable>
                  </View>
                ))
              )}
            </View>
          ) : (
            <>
              <View style={styles.chatHeader}>
                <Pressable onPress={backToModes} hitSlop={8}>
                  <Text style={styles.backText}>{'‹'} Modes</Text>
                </Pressable>
                <Text style={styles.chatHeaderMode}>{MODES.find((m) => m.key === mode)?.label}</Text>
              </View>

              <ScrollView ref={scrollRef} style={styles.messages} contentContainerStyle={styles.messagesContent}>
                {messages.map((m, i) => (
                  <View key={i} style={[styles.bubbleRow, m.role === 'user' ? styles.bubbleRowUser : null]}>
                    <View
                      style={[
                        styles.bubble,
                        m.role === 'user' ? styles.bubbleUser : styles.bubbleAssistant,
                        m.isError ? styles.bubbleError : null,
                      ]}
                    >
                      <Text style={[styles.bubbleText, m.role === 'user' ? styles.bubbleTextUser : null]}>{m.content}</Text>
                    </View>
                  </View>
                ))}
                {sending ? <Text style={styles.working}>Working…</Text> : null}
              </ScrollView>

              {attachError ? <Text style={styles.attachError}>{attachError}</Text> : null}

              {attachments.length > 0 ? (
                <View style={styles.attachRow}>
                  {attachments.map((a) => (
                    <View key={a.name} style={styles.attachChip}>
                      <Text style={styles.attachChipText} numberOfLines={1}>
                        {a.kind === 'image' ? '🖼 ' : '📄 '}{a.name}
                      </Text>
                      <Pressable onPress={() => removeAttachment(a.name)} hitSlop={6}>
                        <Text style={styles.attachChipRemove}>✕</Text>
                      </Pressable>
                    </View>
                  ))}
                </View>
              ) : null}

              <View style={styles.inputRow}>
                <Pressable onPress={pickAttachment} disabled={attaching} style={styles.attachBtn} hitSlop={6}>
                  {attaching ? <ActivityIndicator color={BRAND} size="small" /> : <Text style={styles.attachBtnText}>📎</Text>}
                </Pressable>
                <TextInput
                  style={styles.input}
                  value={input}
                  onChangeText={setInput}
                  placeholder={`Message TKNE (${mode})…`}
                  placeholderTextColor={colors.slate}
                  multiline
                />
                <Pressable
                  onPress={send}
                  disabled={sending || (!input.trim() && attachments.length === 0)}
                  style={[styles.sendBtn, sending || (!input.trim() && attachments.length === 0) ? styles.sendBtnDisabled : null]}
                >
                  {sending ? <ActivityIndicator color={colors.white} /> : <Text style={styles.sendText}>➤</Text>}
                </Pressable>
              </View>
            </>
          )}
        </View>
      ) : null}
    </View>
  );
}

const BRAND = '#4a6d8c';

const styles = StyleSheet.create({
  card: { borderRadius: radii.tile, overflow: 'hidden', backgroundColor: colors.glassFill, marginBottom: 16, ...neu.raised },
  bar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: BRAND, paddingHorizontal: 14, paddingVertical: 12, position: 'relative',
  },
  barText: { color: colors.white, fontSize: 15, fontWeight: '900', letterSpacing: 1 },
  barRight: { position: 'absolute', right: 12, flexDirection: 'row', alignItems: 'center', gap: 12 },
  newBtn: {
    width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.22)',
  },
  newBtnText: { color: colors.white, fontSize: 17, fontWeight: '900', lineHeight: 19 },
  arrow: { color: colors.white, fontSize: 15, fontWeight: '900' },
  arrowOpen: { transform: [{ rotate: '-90deg' }] },

  body: { backgroundColor: colors.glassFill },

  selectList: { padding: 12, gap: 14 },
  topRow: { flexDirection: 'row', gap: 8, marginBottom: 2 },
  topPill: {
    paddingVertical: 6, paddingHorizontal: 14, borderRadius: radii.pill,
    backgroundColor: colors.glassFill, ...neu.raisedSm,
  },
  topPillText: { fontSize: 12, fontWeight: '800', color: colors.blueText },

  listPane: { padding: 12, gap: 10 },
  listLoading: { paddingVertical: 20 },
  listEmpty: { fontSize: 13, color: colors.textMuted, paddingVertical: 12 },
  listRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8,
    paddingVertical: 10, paddingHorizontal: 12, borderRadius: radii.tile,
    backgroundColor: colors.glassFill, ...neu.raisedSm,
  },
  listRowTitle: { flex: 1, fontSize: 13, fontWeight: '700', color: colors.text, fontFamily: fonts.title, textTransform: 'uppercase' },
  listRowMeta: { fontSize: 11, color: colors.slate },

  fileRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 10, paddingHorizontal: 12, borderRadius: radii.tile,
    backgroundColor: colors.glassFill, ...neu.raisedSm,
  },
  fileIcon: { fontSize: 18 },
  fileInfo: { flex: 1, gap: 2 },
  fileDelete: { fontSize: 14, fontWeight: '800', color: colors.slate },
  selectRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  selectPill: {
    minWidth: 92, paddingVertical: 8, paddingHorizontal: 14, borderRadius: radii.pill,
    backgroundColor: colors.glassFill, alignItems: 'center', justifyContent: 'center', ...neu.raisedSm,
  },
  selectPillText: { fontSize: 13, fontWeight: '800', color: colors.blueText },
  selectBlurb: { flex: 1, fontSize: 13, color: colors.textMuted, lineHeight: 18, paddingTop: 8 },

  chatHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 12, paddingTop: 10, paddingBottom: 4,
  },
  backText: { fontSize: 13, fontWeight: '700', color: colors.blueText },
  chatHeaderMode: { fontFamily: fonts.title, fontSize: 12, fontWeight: '800', color: colors.slate, letterSpacing: 0.4, textTransform: 'uppercase' },

  messages: { height: 260, paddingHorizontal: 10 },
  messagesContent: { paddingVertical: 10, gap: 8 },

  bubbleRow: { flexDirection: 'row', justifyContent: 'flex-start' },
  bubbleRowUser: { justifyContent: 'flex-end' },
  bubble: { maxWidth: '85%', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 8 },
  bubbleAssistant: { backgroundColor: colors.glassFill, ...neu.raised },
  bubbleUser: { backgroundColor: BRAND },
  bubbleError: { backgroundColor: colors.glassFill, ...neu.raised },
  bubbleText: { fontSize: 14, color: colors.text, lineHeight: 20 },
  bubbleTextUser: { color: colors.white },
  working: { fontSize: 12, color: colors.slate, fontStyle: 'italic', paddingLeft: 4 },

  attachError: { fontSize: 12, color: colors.danger, paddingHorizontal: 12, paddingTop: 4 },
  attachRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: 8, paddingTop: 6 },
  attachChip: {
    flexDirection: 'row', alignItems: 'center', gap: 6, maxWidth: 160,
    backgroundColor: colors.inputFill, borderRadius: radii.pill, paddingVertical: 5, paddingHorizontal: 10, ...neu.raisedSm,
  },
  attachChipText: { fontSize: 12, fontWeight: '600', color: colors.text, flexShrink: 1 },
  attachChipRemove: { fontSize: 12, fontWeight: '800', color: colors.slate },

  inputRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 8, padding: 8 },
  attachBtn: {
    width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
    backgroundColor: colors.glassFill, ...neu.raisedSm,
  },
  attachBtnText: { fontSize: 18 },
  input: {
    flex: 1, maxHeight: 120, backgroundColor: colors.inputFill, borderRadius: radii.tile,
    paddingHorizontal: 12, paddingVertical: 9,
    fontSize: 14, color: colors.text, ...neu.insetDeep,
  },
  sendBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center', backgroundColor: BRAND },
  sendBtnDisabled: { opacity: 0.4 },
  sendText: { color: colors.white, fontSize: 18, fontWeight: '900' },
});
